import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useSiteContent, useSiteCopy } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-navy-light border border-slate-700 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-colors duration-300";

export default function RequestForm() {
  const { tx } = useSiteCopy();
  const { headings, services, preview } = useSiteContent();
  const serviceOptions = [...services.map((s) => s.title), "Other / Not Sure"];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", emergency: false, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (preview) { toast.info("Preview only — no service request was sent."); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/service-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, email: form.email || null, message: form.message || null }),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Request received — Cody or Don will call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: "", emergency: false, message: "" });
    } catch {
      toast.error("Something went wrong. Please call us directly instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="request" data-testid="request-section" className="py-24 md:py-32 relative overflow-hidden">
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl"
        style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 text-center"
        >
          <p className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-4" data-testid="request-eyebrow">{headings.request.eyebrow}</p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-tight chrome-text">
            {headings.request.title}
          </h2>
          <p className="mt-4 text-slate-400 font-light">
            {headings.request.description}
          </p>
        </motion.div>

        <motion.form
          data-testid="service-request-form"
          onSubmit={submit}
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="metal-card rounded-3xl p-8 md:p-10 space-y-5"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <input
              data-testid="form-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder={tx("request-name", "Your name")}
              aria-label={tx("request-name", "Your name")}
              className={inputCls}
            />
            <input
              data-testid="form-phone-input"
              required
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder={tx("request-phone", "Phone number")}
              aria-label={tx("request-phone", "Phone number")}
              className={inputCls}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <input
              data-testid="form-email-input"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder={tx("request-email", "Email (optional)")}
              aria-label={tx("request-email", "Email (optional)")}
              className={inputCls}
            />
            <select
              data-testid="form-service-select"
              aria-label={tx("request-service", "Service needed")}
              required
              value={form.service}
              onChange={set("service")}
              className={`${inputCls} ${form.service ? "" : "text-slate-500"}`}
            >
              <option value="" disabled>
                {tx("request-service", "Service needed")}
              </option>
              {serviceOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="form-message-input"
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder={tx("request-message", "Describe the problem (optional)")}
            aria-label={tx("request-message", "Describe the problem (optional)")}
            className={`${inputCls} resize-none`}
          />

          <label
            data-testid="form-emergency-checkbox"
            className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition-colors duration-300 ${
              form.emergency ? "border-red-500 bg-red-500/10" : "border-slate-700 bg-navy-light"
            }`}
          >
            <input
              type="checkbox"
              data-testid="form-emergency-input"
              checked={form.emergency}
              onChange={set("emergency")}
              className="w-4 h-4 accent-red-500"
            />
            <AlertTriangle className={`w-4 h-4 ${form.emergency ? "text-red-400" : "text-slate-500"}`} />
            <span className={`text-sm ${form.emergency ? "text-red-300" : "text-slate-400"}`}>
              {tx("request-emergency", "This is an emergency — I need help now")}
            </span>
          </label>

          <button
            data-testid="submit-request-button"
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-3 bg-electric text-navy-deep font-bold text-lg py-4 rounded-2xl hover:scale-[1.01] transition-transform duration-300 glow-blue disabled:opacity-60 disabled:hover:scale-100"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? "Sending..." : tx("request-submit", "Send Service Request")}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
